import React, { useState, useMemo, useEffect } from 'react';
import { GradeLevel, Branch, BankInfo, OnlineRegistration, ProgramPrice } from '../types/db';
import {
  X,
  CheckCircle,
  Landmark,
  ImageIcon,
  Loader2,
  ArrowRight,
  User,
  Phone,
  MapPin,
  GraduationCap,
  Sparkles,
  ChevronDown,
  School,
  AlertCircle,
  DollarSign,
} from 'lucide-react';
import { supabase } from '../lib/supabaseClient';

const normalizeGrade = (grade: string) => {
  const mapping: Record<string, string> = {
    'SD Kelas 5': '5 SD',
    'SD Kelas 6': '6 SD',
    'SMP Kelas 7': '7 SMP',
    'SMP Kelas 8': '8 SMP',
    'SMP Kelas 9': '9 SMP',
    'SMA Kelas 10': '10 SMA',
    'SMA Kelas 11 IPA': '11 SMA',
    'SMA Kelas 11 IPS': '11 SMA',
    'SMA Kelas 12 IPA': '12 SMA',
    'SMA Kelas 12 IPS': '12 SMA',
    'ALUMNI (GAP YEAR)': '12 SMA',
  };
  return mapping[grade] || grade;
};

const parseDurations = (input: any): number[] => {
  if (!input) return [];
  if (Array.isArray(input)) return input.map(Number);
  if (typeof input === 'string') {
    try {
      // Handle JSON string format "[3,6,8]"
      const parsed = JSON.parse(input);
      if (Array.isArray(parsed)) return parsed.map(Number);
    } catch (e) {
      // Handle comma separated format "3,6,8"
      return input
        .split(',')
        .map((s) => parseInt(s.trim()))
        .filter((n) => !isNaN(n));
    }
    return input
      .split(',')
      .map((s) => parseInt(s.trim()))
      .filter((n) => !isNaN(n));
  }
  return [];
};

interface PublicRegistrationProps {
  branches: Branch[];
  onRegister: (data: OnlineRegistration) => void;
  onClose: () => void;
  programPrices?: ProgramPrice[];
}

const FALLBACK_BANK: BankInfo = {
  bankName: 'BANK CENTRAL ASIA (BCA)',
  accountNumber: '123-456-7890',
  accountHolder: 'YAYASAN PENDIDIKAN DELTA',
};

export const PublicRegistration: React.FC<PublicRegistrationProps> = ({
  branches = [],
  onRegister,
  onClose,
  programPrices: initialProgramPrices = [],
}) => {
  const [step, setStep] = useState(1);
  const [isUploading, setIsUploading] = useState(false);
  const [proofFile, setProofFile] = useState<File | null>(null);
  const [programPrices, setProgramPrices] = useState<ProgramPrice[]>(initialProgramPrices);
  const [bankAccounts, setBankAccounts] = useState<any[]>([]);

  const [formData, setFormData] = useState({
    branchId: '',
    name: '',
    grade: GradeLevel.SMA_10,
    phoneNumber: '',
    address: '',
    schoolOrigin: '',
    parentName: '',
    parentPhone: '',
    programChoice: '',
    customProgram: '',
    paymentMethod: 'CASH' as 'CASH' | 'ANGSUR',
    installmentDuration: 0,
  });

  useEffect(() => {
    const loadPrices = async () => {
      const { data, error } = await supabase.from('program_prices').select('*');
      if (error) {
        /* console removed */
      }
      if (data) setProgramPrices(data);
    };
    const loadBanks = async () => {
      const { data, error } = await supabase.from('bank_accounts').select('*');
      if (error) {
        /* console removed */
      }
      if (data) setBankAccounts(data);
    };
    loadPrices();
    loadBanks();
  }, []);

  const filteredBranches = useMemo(() => {
    return branches.filter(
      (b) =>
        b.id !== 'HQ' &&
        b.id !== 'HEADQUARTERS' &&
        !b.name?.toLowerCase().includes('pusat') &&
        b.is_active !== false
    );
  }, [branches]);

  const activeBank = useMemo(() => {
    if (!formData.branchId) return FALLBACK_BANK;

    const branchBank = bankAccounts.find((b) => b.branch_id === formData.branchId);
    if (branchBank) {
      return {
        bankName: branchBank.bank_name,
        accountNumber: branchBank.account_number,
        accountHolder: branchBank.account_holder,
      };
    }

    const hqBank = bankAccounts.find((b) => b.branch_id === 'HQ');
    if (hqBank) {
      return {
        bankName: hqBank.bank_name,
        accountNumber: hqBank.account_number,
        accountHolder: hqBank.account_holder,
      };
    }

    return FALLBACK_BANK;
  }, [formData.branchId, bankAccounts]);

  const availablePrograms = useMemo(() => {
    if (!formData.branchId || !formData.grade) return [];
    const normalizedGrade = normalizeGrade(formData.grade);
    const filtered = programPrices.filter((p) => {
      if (p.branch_id.toUpperCase() !== formData.branchId.toUpperCase()) return false;
      if (p.grade === 'ALL') return true;
      if (p.grade === normalizedGrade) return true;

      if (formData.grade.includes('SD') && p.grade === 'SD') return true;
      if (formData.grade.includes('SMP') && p.grade === 'SMP') return true;
      if (formData.grade.includes('SMA') && p.grade === 'SMA') return true;

      return false;
    });

    if (programPrices.length > 0 && filtered.length === 0) {
      /* console removed */
    }

    return filtered;
  }, [programPrices, formData.branchId, formData.grade]);

  const priceDetails = useMemo(() => {
    if (!formData.branchId || !formData.programChoice || !formData.grade) {
      return { courseFee: 0, regFee: 0, total: 0 };
    }

    const normalizedGrade = normalizeGrade(formData.grade);
    const match = programPrices.find(
      (p) =>
        p.branch_id === formData.branchId &&
        p.program_name.toUpperCase() === formData.programChoice.toUpperCase() &&
        (p.grade === normalizedGrade ||
          (formData.grade.includes('SD') && p.grade === 'SD') ||
          (formData.grade.includes('SMP') && p.grade === 'SMP') ||
          (formData.grade.includes('SMA') && p.grade === 'SMA') ||
          p.grade === 'ALL')
    );

    if (!match) return { courseFee: 0, regFee: 0, total: 0, match: null };

    const courseFee =
      formData.paymentMethod === 'ANGSUR'
        ? Number(match.installment_price) || Number(match.price)
        : Number(match.cash_price) || Number(match.price);
    const regFee = Number(match.registration_fee) || 0;

    return { courseFee, regFee, total: courseFee + regFee, match };
  }, [
    formData.branchId,
    formData.programChoice,
    formData.grade,
    formData.paymentMethod,
    programPrices,
  ]);

  const handleNext = () => {
    if (step === 1) {
      if (!formData.name || !formData.phoneNumber || !formData.branchId || !formData.schoolOrigin) {
        return window.alert('Mohon lengkapi profil siswa terlebih dahulu.');
      }
      setStep(2);
    } else if (step === 2) {
      if (!formData.parentName || !formData.parentPhone) {
        return window.alert('Data kontak orang tua wajib diisi.');
      }
      setStep(3);
    } else if (step === 3) {
      if (!formData.programChoice) {
        return window.alert('Pilih salah satu paket belajar untuk melanjutkan.');
      }
      setStep(4);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!proofFile) return window.alert('Mohon unggah bukti pembayaran.');
    setIsUploading(true);
    const regId = 'REG-' + Date.now();

    try {
      const fileExt = proofFile.name.split('.').pop();
      const filePath = `registrations/${regId}.${fileExt}`;
      await supabase.storage.from('learning-files').upload(filePath, proofFile);

      const payload: OnlineRegistration = {
        id: regId,
        branch_id: formData.branchId,
        name: formData.name,
        grade: formData.grade,
        phone_number: formData.phoneNumber,
        address: formData.address,
        school_origin: formData.schoolOrigin,
        parent_name: formData.parentName,
        parent_phone: formData.parentPhone,
        program_choice: formData.programChoice,
        total_cost: priceDetails.total,
        payment_method: formData.paymentMethod,
        installment_count: formData.paymentMethod === 'ANGSUR' ? formData.installmentDuration : 1,
        status: 'PENDING',
        submitted_at: new Date().toISOString(),
        transfer_proof_path: filePath,
      };

      const { error } = await supabase.from('online_registrations').insert(payload);
      if (error) throw error;

      onRegister(payload);
      setStep(5);
    } catch (err: any) {
      window.alert('Terjadi Kesalahan: ' + err.message);
    } finally {
      setIsUploading(false);
    }
  };

  if (step === 5)
    return (
      <div className="fixed inset-0 bg-indigo-950 z-[100] flex items-center justify-center p-4 text-white">
        <div className="bg-white p-12 rounded-[4rem] max-w-lg w-full text-center shadow-2xl animate-fade-in border-4 border-indigo-50">
          <div className="w-24 h-24 bg-emerald-50 text-emerald-500 rounded-full flex items-center justify-center mx-auto mb-8 shadow-inner">
            <CheckCircle className="w-12 h-12" />
          </div>
          <h2 className="text-3xl font-black text-slate-900 mb-4 uppercase tracking-tighter">
            Pendaftaran Berhasil
          </h2>
          <p className="text-slate-400 text-sm mb-10 font-bold uppercase tracking-widest leading-relaxed">
            Admin cabang akan memverifikasi bukti bayar anda dalam waktu 1x24 jam.
          </p>
          <button
            onClick={onClose}
            className="w-full bg-slate-900 text-white py-5 rounded-3xl font-black uppercase tracking-widest text-xs hover:bg-black transition-all"
          >
            TUTUP
          </button>
        </div>
      </div>
    );

  return (
    <div className="fixed inset-0 bg-slate-900/80 z-[100] flex items-center justify-center p-4 backdrop-blur-xl overflow-y-auto no-scrollbar">
      <div className="bg-white w-full max-w-5xl rounded-[4rem] shadow-2xl my-8 overflow-hidden flex flex-col md:flex-row relative border border-white">
        <button
          onClick={onClose}
          className="absolute top-8 right-8 z-20 p-3 bg-slate-50 text-slate-400 rounded-full hover:bg-slate-200 transition-all"
        >
          <X className="w-6 h-6" />
        </button>

        <div className="md:w-1/3 bg-indigo-600 p-12 text-white flex flex-col justify-between shrink-0 relative overflow-hidden">
          <div className="relative z-10">
            <Sparkles className="w-12 h-12 text-yellow-300 mb-6" />
            <h2 className="text-3xl font-black uppercase tracking-tighter leading-none mb-2">
              Delta Online
              <br />
              Registration
            </h2>
            <p className="text-[10px] font-bold text-indigo-200 uppercase tracking-[0.3em]">
              Integrasi Digital Network
            </p>
          </div>
          <div className="space-y-8 relative z-10">
            <StepIndicator active={step >= 1} num={1} label="Identitas Siswa" />
            <StepIndicator active={step >= 2} num={2} label="Data Orang Tua" />
            <StepIndicator active={step >= 3} num={3} label="Program Belajar" />
            <StepIndicator active={step >= 4} num={4} label="Pembayaran" />
          </div>
        </div>

        <div className="md:w-2/3 p-10 md:p-16 bg-white overflow-y-auto no-scrollbar max-h-[90vh]">
          {step === 1 && (
            <div className="space-y-10 animate-fade-in">
              <h3 className="text-3xl font-black text-slate-900 uppercase tracking-tighter">
                Profil Calon Siswa
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <SelectGroup
                  icon={<MapPin />}
                  label="Cabang Tujuan"
                  value={formData.branchId}
                  options={filteredBranches.map((b) => ({ v: b.id, l: b.name }))}
                  onChange={(v) => setFormData({ ...formData, branchId: v, programChoice: '' })}
                />
                <SelectGroup
                  icon={<GraduationCap />}
                  label="Grade / Kelas"
                  value={formData.grade}
                  options={Object.values(GradeLevel).map((g) => ({ v: g, l: g }))}
                  onChange={(v) =>
                    setFormData({ ...formData, grade: v as GradeLevel, programChoice: '' })
                  }
                />
                <InputGroup
                  icon={<User />}
                  label="Nama Lengkap"
                  value={formData.name}
                  onChange={(v) => setFormData({ ...formData, name: v })}
                />
                <InputGroup
                  icon={<School />}
                  label="Sekolah Asal"
                  value={formData.schoolOrigin}
                  onChange={(v) => setFormData({ ...formData, schoolOrigin: v })}
                />
                <div className="md:col-span-2">
                  <InputGroup
                    icon={<Phone />}
                    label="WhatsApp Aktif"
                    value={formData.phoneNumber}
                    onChange={(v) => setFormData({ ...formData, phoneNumber: v })}
                  />
                </div>
              </div>
              <button
                onClick={handleNext}
                className="w-full bg-indigo-600 text-white py-6 rounded-3xl font-black uppercase text-xs tracking-[0.3em] flex justify-center items-center gap-4 shadow-2xl shadow-indigo-100 hover:bg-indigo-700 transition-all"
              >
                Lanjut ke Biodata <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-10 animate-fade-in">
              <h3 className="text-3xl font-black text-slate-900 uppercase tracking-tighter">
                Biodata Orang Tua
              </h3>
              <div className="space-y-6">
                <InputGroup
                  icon={<User />}
                  label="Nama Orang Tua"
                  value={formData.parentName}
                  onChange={(v) => setFormData({ ...formData, parentName: v })}
                />
                <InputGroup
                  icon={<Phone />}
                  label="WhatsApp Orang Tua"
                  value={formData.parentPhone}
                  onChange={(v) => setFormData({ ...formData, parentPhone: v })}
                />
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-indigo-600 uppercase ml-2 tracking-widest">
                    Alamat Lengkap
                  </label>
                  <textarea
                    className="w-full border-2 border-slate-50 p-5 rounded-[2rem] font-bold bg-slate-50/50 outline-none h-36 resize-none focus:border-indigo-100"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex gap-4">
                <button
                  onClick={() => setStep(1)}
                  className="px-10 py-6 bg-slate-100 text-slate-400 rounded-3xl font-black uppercase text-[10px] tracking-widest"
                >
                  Kembali
                </button>
                <button
                  onClick={handleNext}
                  className="flex-1 bg-indigo-600 text-white py-6 rounded-3xl font-black uppercase text-xs tracking-[0.3em] shadow-2xl shadow-indigo-100 hover:bg-indigo-700"
                >
                  Lanjut ke Program
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-10 animate-fade-in">
              <h3 className="text-3xl font-black text-slate-900 uppercase tracking-tighter">
                Pilih Paket Belajar
              </h3>
              <div className="space-y-8">
                <div className="grid grid-cols-1 gap-4">
                  {availablePrograms.map((p) => (
                    <button
                      key={p.program_name}
                      onClick={() => setFormData({ ...formData, programChoice: p.program_name })}
                      className={`p-8 rounded-[2.5rem] border-2 text-left transition-all flex justify-between items-center group ${formData.programChoice === p.program_name ? 'border-indigo-600 bg-indigo-50' : 'border-slate-50 hover:border-indigo-200'}`}
                    >
                      <div>
                        <p
                          className={`font-black uppercase tracking-tight text-xl ${formData.programChoice === p.program_name ? 'text-indigo-900' : 'text-slate-400'}`}
                        >
                          {p.program_name}
                        </p>
                        <p className="text-[10px] text-slate-400 font-bold uppercase mt-1">
                          Grade {p.grade}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-black text-indigo-600 text-lg">
                          Rp {(p.cash_price || p.price).toLocaleString()}
                        </p>
                        {p.installment_price && (
                          <p className="text-[8px] font-black text-amber-500 uppercase">
                            Angsur: Rp {p.installment_price.toLocaleString()}
                          </p>
                        )}
                        <p className="text-[9px] text-slate-400 uppercase">
                          Regis Rp {Number(p.registration_fee).toLocaleString()}
                        </p>
                        <div
                          className={`mt-2 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ml-auto ${formData.programChoice === p.program_name ? 'bg-indigo-600 border-indigo-600 text-white shadow-lg' : 'border-slate-200'}`}
                        >
                          {formData.programChoice === p.program_name && (
                            <CheckCircle className="w-4 h-4" />
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                  {availablePrograms.length === 0 && (
                    <div className="py-20 text-center border-4 border-dashed rounded-[3rem] border-slate-50 bg-slate-50/20">
                      <p className="text-rose-400 font-black uppercase text-[10px] tracking-widest">
                        Tidak ada program aktif untuk cabang & grade ini.
                      </p>
                    </div>
                  )}
                </div>

                {formData.programChoice && priceDetails.match && (
                  <div className="space-y-6 animate-fade-in">
                    <div className="grid grid-cols-2 gap-4">
                      <button
                        onClick={() => setFormData({ ...formData, paymentMethod: 'CASH' })}
                        className={`p-6 rounded-3xl border-2 transition-all text-center ${formData.paymentMethod === 'CASH' ? 'border-indigo-600 bg-indigo-50' : 'border-slate-50 hover:border-indigo-100'}`}
                      >
                        <p
                          className={`text-[10px] font-black uppercase tracking-widest mb-1 ${formData.paymentMethod === 'CASH' ? 'text-indigo-600' : 'text-slate-400'}`}
                        >
                          Bayar Tunai
                        </p>
                        <p className="font-black text-slate-900">CASH</p>
                      </button>
                      <button
                        onClick={() => {
                          const durations = priceDetails.match?.installment_durations || [];
                          setFormData({
                            ...formData,
                            paymentMethod: 'ANGSUR',
                            installmentDuration: durations.length > 0 ? durations[0] : 0,
                          });
                        }}
                        className={`p-6 rounded-3xl border-2 transition-all text-center ${formData.paymentMethod === 'ANGSUR' ? 'border-indigo-600 bg-indigo-50' : 'border-slate-50 hover:border-indigo-100'}`}
                      >
                        <p
                          className={`text-[10px] font-black uppercase tracking-widest mb-1 ${formData.paymentMethod === 'ANGSUR' ? 'text-indigo-600' : 'text-slate-400'}`}
                        >
                          Bayar Bertahap
                        </p>
                        <p className="font-black text-slate-900">ANGSURAN</p>
                      </button>
                    </div>

                    {formData.paymentMethod === 'ANGSUR' &&
                      parseDurations(priceDetails.match.installment_durations).length > 0 && (
                        <div className="space-y-3">
                          <label className="text-[10px] font-black text-indigo-600 uppercase ml-2 tracking-widest">
                            Pilih Tenor Angsuran
                          </label>
                          <div className="flex gap-3 overflow-x-auto pb-2 no-scrollbar">
                            {parseDurations(priceDetails.match.installment_durations).map((d) => (
                              <button
                                key={d}
                                onClick={() => setFormData({ ...formData, installmentDuration: d })}
                                className={`px-6 py-3 rounded-xl font-black text-xs transition-all shrink-0 ${formData.installmentDuration === d ? 'bg-indigo-600 text-white shadow-lg' : 'bg-slate-100 text-slate-400 hover:bg-slate-200'}`}
                              >
                                {d}X ANGSUR
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                    <div className="bg-slate-900 p-10 rounded-[3.5rem] text-white shadow-2xl relative overflow-hidden">
                      <p className="text-[10px] font-black text-indigo-400 uppercase tracking-[0.3em] mb-6 flex items-center gap-3">
                        <DollarSign className="w-4 h-4" /> Ringkasan Invoice
                      </p>
                      <div className="space-y-4 border-b border-white/10 pb-6">
                        <div className="flex justify-between items-center text-sm font-bold opacity-60">
                          <span>
                            Biaya Program ({formData.programChoice}) - {formData.paymentMethod}
                          </span>
                          <span>Rp {priceDetails.courseFee.toLocaleString()}</span>
                        </div>
                        {formData.paymentMethod === 'ANGSUR' &&
                          formData.installmentDuration > 0 && (
                            <div className="flex justify-between items-center text-[10px] font-black text-indigo-400 uppercase tracking-widest">
                              <span>Estimasi Per Angsuran ({formData.installmentDuration}x)</span>
                              <span>
                                Rp{' '}
                                {Math.ceil(
                                  priceDetails.courseFee / formData.installmentDuration
                                ).toLocaleString()}
                              </span>
                            </div>
                          )}
                        <div className="flex justify-between items-center text-sm font-bold opacity-60">
                          <span>Biaya Registrasi Pendaftaran</span>
                          <span>Rp {priceDetails.regFee.toLocaleString()}</span>
                        </div>
                      </div>
                      <div className="pt-6 flex justify-between items-end">
                        <div>
                          <p className="text-[10px] font-black uppercase text-indigo-400 tracking-widest mb-1">
                            Total Yang Harus Dibayar
                          </p>
                          <p className="text-4xl font-black tracking-tighter">
                            Rp {priceDetails.total.toLocaleString()}
                          </p>
                        </div>
                        <Sparkles className="text-yellow-400 w-10 h-10 mb-2 animate-pulse" />
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex gap-4">
                <button
                  onClick={() => setStep(2)}
                  className="px-10 py-6 bg-slate-100 text-slate-400 rounded-3xl font-black uppercase text-[10px] tracking-widest"
                >
                  Kembali
                </button>
                <button
                  onClick={handleNext}
                  disabled={!formData.programChoice}
                  className="flex-1 bg-indigo-600 text-white py-6 rounded-3xl font-black uppercase text-xs tracking-[0.3em] shadow-2xl shadow-indigo-100 hover:bg-indigo-700 disabled:opacity-50"
                >
                  Lanjut Pembayaran
                </button>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-10 animate-fade-in">
              <h3 className="text-3xl font-black text-slate-900 uppercase tracking-tighter">
                Metode Pembayaran
              </h3>
              <div className="space-y-8">
                <div className="bg-indigo-900 p-10 rounded-[3.5rem] text-white relative overflow-hidden shadow-2xl border-b-8 border-indigo-950">
                  <div className="flex items-center gap-5 mb-8">
                    <Landmark className="w-10 h-10 text-indigo-300" />
                    <p className="text-lg font-black uppercase tracking-widest">
                      {activeBank.bankName}
                    </p>
                  </div>
                  <p className="text-4xl md:text-5xl font-black tracking-[0.1em] font-mono mb-3">
                    {activeBank.accountNumber}
                  </p>
                  <p className="text-[11px] font-bold text-indigo-300 uppercase tracking-[0.3em]">
                    Atas Nama: {activeBank.accountHolder}
                  </p>
                </div>

                <div className="space-y-4">
                  <label className="text-[10px] font-black text-slate-400 uppercase ml-3 tracking-widest flex items-center gap-2">
                    <ImageIcon className="w-4 h-4" /> Bukti Pembayaran (Screenshot/Foto)
                  </label>
                  <label className="border-4 border-dashed border-slate-100 rounded-[3rem] p-12 flex flex-col items-center justify-center gap-5 cursor-pointer hover:bg-slate-50 transition-all text-slate-300 group shadow-inner">
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={(e) => setProofFile(e.target.files?.[0] || null)}
                    />
                    {proofFile ? (
                      <div className="text-center animate-bounce">
                        <CheckCircle className="w-14 h-14 text-emerald-500 mx-auto mb-3" />
                        <p className="font-black text-slate-900 uppercase text-[10px] truncate max-w-[250px]">
                          {proofFile.name}
                        </p>
                      </div>
                    ) : (
                      <>
                        <ImageIcon className="w-14 h-14 group-hover:scale-110 transition-transform" />
                        <p className="font-black text-[11px] uppercase tracking-widest text-slate-400">
                          Klik Untuk Unggah File
                        </p>
                      </>
                    )}
                  </label>
                </div>

                <div className="bg-amber-50 p-6 rounded-3xl border border-amber-100 flex items-start gap-4">
                  <AlertCircle className="w-6 h-6 text-amber-600 shrink-0 mt-0.5" />
                  <p className="text-[11px] text-amber-800 font-bold leading-relaxed uppercase tracking-tight">
                    Mohon unggah struk transfer asli. Pembayaran akan divalidasi oleh sistem dalam
                    waktu 1x24 jam.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <button
                  onClick={() => setStep(3)}
                  className="px-10 py-6 bg-slate-100 text-slate-400 rounded-3xl font-black uppercase text-[10px] tracking-widest"
                >
                  Kembali
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={isUploading || !proofFile}
                  className="flex-1 bg-emerald-600 text-white py-6 rounded-3xl font-black uppercase text-xs tracking-[0.3em] shadow-2xl shadow-emerald-100 hover:bg-emerald-700 disabled:opacity-50 flex justify-center items-center gap-4 transition-all"
                >
                  {isUploading ? (
                    <Loader2 className="animate-spin w-6 h-6" />
                  ) : (
                    <CheckCircle className="w-6 h-6" />
                  )}{' '}
                  {isUploading ? 'MENGIRIM DATA...' : 'SELESAIKAN PENDAFTARAN'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const StepIndicator = ({ active, num, label }: { active: boolean; num: number; label: string }) => (
  <div
    className={`flex items-center gap-5 transition-all duration-700 ${active ? 'opacity-100 translate-x-3' : 'opacity-30'}`}
  >
    <div
      className={`w-10 h-10 rounded-2xl flex items-center justify-center font-black text-sm border-2 ${active ? 'bg-white text-indigo-600 border-white shadow-lg' : 'border-white/30 text-white'}`}
    >
      {num}
    </div>
    <p className="font-black uppercase tracking-[0.2em] text-[10px]">{label}</p>
  </div>
);

const InputGroup = ({
  icon,
  label,
  value,
  onChange,
  placeholder,
}: {
  icon: any;
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) => (
  <div className="space-y-2">
    <label className="text-[10px] font-black text-indigo-600 uppercase ml-2 tracking-widest">
      {label}
    </label>
    <div className="relative">
      <div className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-300 w-5 h-5">{icon}</div>
      <input
        className="w-full border-2 border-slate-50 pl-14 pr-5 py-5 rounded-3xl font-bold bg-slate-50/50 outline-none focus:ring-4 focus:ring-indigo-500/5 focus:border-indigo-100 transition-all text-xs"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </div>
  </div>
);

const SelectGroup = ({
  icon,
  label,
  value,
  options,
  onChange,
}: {
  icon: any;
  label: string;
  value: string;
  options: { v: string; l: string }[];
  onChange: (v: string) => void;
}) => (
  <div className="space-y-2">
    <label className="text-[10px] font-black text-indigo-600 uppercase ml-2 tracking-widest">
      {label}
    </label>
    <div className="relative">
      <div className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-300 w-5 h-5">{icon}</div>
      <select
        className="w-full border-2 border-slate-50 pl-14 pr-12 py-5 rounded-3xl font-black bg-slate-50/50 appearance-none outline-none focus:ring-4 focus:ring-indigo-500/5 focus:border-indigo-100 transition-all text-xs"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">-- Pilih --</option>
        {options.map((o) => (
          <option key={o.v} value={o.v}>
            {o.l.toUpperCase()}
          </option>
        ))}
      </select>
      <ChevronDown className="absolute right-5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-300 pointer-events-none" />
    </div>
  </div>
);
